import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { SalesDoc, SalesDocFolder, CreateSalesDocPayload, UpdateSalesDocPayload, Payment, CreatePaymentPayload } from '../types';

/** Canonical key — list page, detail drawer, and any future "Recent Docs" widget share it. */
export const SALES_DOCS_KEY = ['salesDocs', 'list'] as const;

/**
 * Single canonical Sales Documents hook. Returns the full list (backend scopes
 * by role/department). A future "Recent Sales Documents" dashboard widget can
 * consume this exact hook/cache (e.g. `data.slice(0, n)`) with no changes.
 */
export interface UseSalesDocsOptions {
  type?: string;
  relatedToType?: 'lead' | 'deal' | null;
  relatedToId?: string | null;
  /** A folder id, or 'unfiled' for documents outside any folder. Omit for every document regardless of folder. */
  folderId?: string | null;
}

export function useSalesDocs(opts?: UseSalesDocsOptions) {
  return useQuery({
    queryKey: ['salesDocs', 'list', opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.type) params.append('type', opts.type);
      if (opts?.relatedToType) params.append('relatedToType', opts.relatedToType);
      if (opts?.relatedToId) params.append('relatedToId', opts.relatedToId);
      if (opts?.folderId) params.append('folder_id', opts.folderId);
      const q = params.toString() ? `?${params.toString()}` : '';
      const { data } = await api.get<{ docs: SalesDoc[] }>(`/api/sales-docs${q}`);
      return data.docs ?? [];
    },
    staleTime: 60_000,
  });
}

export function useSalesDocFolders() {
  return useQuery({
    queryKey: ['salesDocs', 'folders'],
    queryFn: async () => {
      const { data } = await api.get<{ folders: SalesDocFolder[] }>('/api/sales-docs/folders');
      return data.folders ?? [];
    },
    staleTime: 60_000,
  });
}

export function useSalesDocFolderMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['salesDocs'] });

  const createFolder = useMutation({
    mutationFn: async (name: string) => {
      const { data } = await api.post<{ ok: boolean; folder: SalesDocFolder }>('/api/sales-docs/folders', { name });
      return data.folder;
    },
    onSuccess: invalidate,
  });

  const renameFolder = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/sales-docs/folders/${id}`, { name });
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteFolder = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/sales-docs/folders/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createFolder, renameFolder, deleteFolder };
}

export function useSalesDocMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['salesDocs'] });

  const createDoc = useMutation({
    mutationFn: async (payload: CreateSalesDocPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/sales-docs', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateDoc = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateSalesDocPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/sales-docs/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteDoc = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/sales-docs/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createDoc, updateDoc, deleteDoc };
}

/** Optional file upload via the EXISTING generic endpoint; returns the stored URL. */
export function useFileUpload() {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post<{ url: string }>('/api/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.url;
    },
  });
}

export function usePayments(opts?: { invoiceId?: string }) {
  return useQuery({
    queryKey: ['payments', 'list', opts],
    queryFn: async () => {
      const q = opts?.invoiceId ? `?invoiceId=${opts.invoiceId}` : '';
      const { data } = await api.get<{ payments: Payment[] }>(`/api/payments${q}`);
      return data.payments ?? [];
    },
    staleTime: 30_000,
  });
}

export function usePaymentMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['payments'] }),
    queryClient.invalidateQueries({ queryKey: ['salesDocs'] }),
    // Also invalidate accounts metrics since payment updates them
    queryClient.invalidateQueries({ queryKey: ['accountsData'] }),
  ]);

  const logPayment = useMutation({
    mutationFn: async (payload: CreatePaymentPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/payments', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deletePayment = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/payments/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { logPayment, deletePayment };
}
